/**
 * Open marks and settled ones.
 *
 * Marks only ever accumulated. Six people reading a forty-page agreement
 * produce hundreds, and the only way to clear one was to delete it — which
 * throws away the reasoning that settled it. So a clause argued over and
 * answered read identically to one nobody had touched.
 *
 * Resolving is neither deleting nor hiding. What changes here is order and
 * count: a settled mark sinks below the live ones and stops being counted as
 * work outstanding, and the note that settled it stays attached to it.
 */

/** Only the field that decides it, so this works on a mark at any stage. */
interface Settleable {
  resolvedAt: string | null;
}

export function isSettled(mark: Settleable): boolean {
  return mark.resolvedAt !== null && mark.resolvedAt !== '';
}

/**
 * Live marks first, settled ones after, each group in the order it arrived.
 *
 * Stable within a group because the server orders by position in the clause,
 * and a list of marks that jumps around between renders is a list nobody
 * trusts. Array.prototype.sort is stable in every engine this ships to, but
 * partitioning says so without depending on it.
 */
export function openFirst<T extends Settleable>(marks: readonly T[]): T[] {
  const open: T[] = [];
  const settled: T[] = [];
  for (const mark of marks) (isSettled(mark) ? settled : open).push(mark);
  return [...open, ...settled];
}

export interface MarkCount {
  total: number;
  open: number;
  settled: number;
  /** The line above the list. Empty when there is nothing to say. */
  label: string;
}

/**
 * How much of this clause is still live.
 *
 * "4 marks on this clause" reads the same whether all four are open questions
 * or all four were answered in March, and which of those it is decides whether
 * a rep stops here. The settled count is stated separately rather than folded
 * into the total, because a mark that has been dealt with is still evidence
 * the clause was contested.
 */
export function markCount(marks: readonly Settleable[]): MarkCount {
  const total = marks.length;
  const settled = marks.filter(isSettled).length;
  const open = total - settled;

  if (total === 0) return { total, open, settled, label: '' };

  const marksWord = total === 1 ? '1 mark on this clause' : `${total} marks on this clause`;
  if (settled === 0) return { total, open, settled, label: marksWord };
  if (open === 0) return { total, open, settled, label: `${marksWord} · all settled` };
  return { total, open, settled, label: `${marksWord} · ${settled} settled` };
}
